import ReactECharts from 'echarts-for-react';

/**
 * CostGapChart — "dark command-deck" variant (_3).
 *
 * Same data and chart family as CostGapChart (ECharts stacked-bar waterfall:
 * fully staffed $1.185M → savings −$1.161M → Raisy $24K). Restyled as a
 * confident dashboard: opaque slate-900 surface, light text, teal/cyan
 * accents, subtle gridlines.
 *
 * Data note: values are identical to the original (HUMAN 1,185,000;
 * RAISY 24,000; SAVINGS 1,161,000 = 98%) — nothing invented.
 */

const BG = '#0F172A'; // slate-900 card surface
const TEXT = '#F8FAFC'; // primary light text
const SECONDARY = '#94A3B8'; // slate-400 — secondary text
const GRID = '#1E293B'; // slate-800 — subtle gridlines
const TEAL = '#0AB3BF';
const CYAN = '#4DD4E0';

const HUMAN = 1185000;
const RAISY = 24000;
const SAVINGS = HUMAN - RAISY; // 1,161,000 (98%)

/**
 * ONE shared money formatter — used by the tooltip (hover) AND every axis tick
 * AND the bar labels, so the numbers can never disagree (RULES_BLOG_POST
 * absolute-consistency rule).
 */
const formatMoney = (v: number): string => {
  const sign = v < 0 ? '−' : '';
  const a = Math.abs(v);
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(0)}K`;
  return `${sign}$${a}`;
};

export const CostGapChart_3 = ({ height = 340 }: { height?: number }) => {
  const option = {
    backgroundColor: BG,
    tooltip: {
      trigger: 'item',
      backgroundColor: '#1E293B',
      borderColor: '#334155',
      textStyle: { color: TEXT, fontSize: 13 },
      formatter: (p: { dataIndex: number }) => {
        const labels = ['Fully staffed', 'Savings (98%)', 'Raisy department'];
        const vals = [HUMAN, -SAVINGS, RAISY];
        return `${labels[p.dataIndex]}<br/><b>${formatMoney(vals[p.dataIndex])}</b>`;
      },
    },
    grid: { left: 50, right: 24, top: 30, bottom: 44 },
    xAxis: {
      type: 'category',
      data: ['Fully staffed', 'Savings (98%)', 'Raisy department'],
      axisLine: { lineStyle: { color: '#334155' } },
      axisLabel: { color: SECONDARY, fontSize: 13, fontWeight: 600 },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'annual cost (USD)',
      nameTextStyle: { color: SECONDARY, fontSize: 11 },
      axisLabel: {
        color: SECONDARY,
        formatter: (v: number) => formatMoney(v),
      },
      splitLine: { lineStyle: { color: GRID } },
    },
    series: [
      // invisible base keeps the running total (stacked-bar waterfall technique)
      {
        name: 'base',
        type: 'bar',
        stack: 'waterfall',
        itemStyle: { color: 'transparent', borderColor: 'transparent' },
        silent: true,
        data: [0, HUMAN, 0],
      },
      {
        name: 'value',
        type: 'bar',
        stack: 'waterfall',
        barWidth: '46%',
        data: [
          { value: HUMAN, itemStyle: { color: SECONDARY, borderRadius: [4, 4, 0, 0] } },
          // negative → drawn downward from the running total (the savings drop)
          {
            value: -SAVINGS,
            itemStyle: { color: CYAN, borderRadius: 0 },
            label: {
              show: true,
              position: 'inside',
              color: BG,
              fontWeight: 700,
              fontSize: 13,
              formatter: '98% saved',
            },
          },
          { value: RAISY, itemStyle: { color: TEAL, borderRadius: [4, 4, 0, 0] } },
        ],
        label: {
          show: true,
          position: 'top',
          color: TEXT,
          fontWeight: 'bold',
          fontSize: 14,
          formatter: (p: { dataIndex: number }) =>
            p.dataIndex === 1 ? '' : formatMoney(p.dataIndex === 0 ? HUMAN : RAISY),
        },
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: CYAN, type: 'dashed' },
          label: {
            color: SECONDARY,
            fontSize: 11,
            formatter: `you pay ${formatMoney(RAISY)}`,
          },
          data: [{ yAxis: RAISY }],
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-700 bg-slate-900 p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-400 text-center">
        Figure 1. The 98% cost gap as a waterfall — fully staffed $1.185M drops by the savings to Raisy $24K. Hover for values.
      </p>
    </div>
  );
};
